/**
 * Persistence for the offline scan queue: IndexedDB database `macksons-operator`, object
 * store `scans` keyed by `idempotencyKey` (design "Clients → Operator_Console"). A memory
 * store with the same contract backs unit and property tests that do not need IndexedDB.
 *
 * Every write is a single-record `put`, so an item's state change is atomic: a crash leaves
 * the item either in its previous or its next state, never half-written.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { QueuedScan } from './scan-queue';

export interface ScanStore {
  all(): Promise<QueuedScan[]>;
  get(idempotencyKey: string): Promise<QueuedScan | undefined>;
  put(scan: QueuedScan): Promise<void>;
  delete(idempotencyKey: string): Promise<void>;
}

interface OperatorDb extends DBSchema {
  scans: { key: string; value: QueuedScan };
}

export const SCAN_DB_NAME = 'macksons-operator';
export const SCAN_STORE_NAME = 'scans';

export function createIdbScanStore(dbName: string = SCAN_DB_NAME): ScanStore {
  let db: Promise<IDBPDatabase<OperatorDb>> | undefined;
  const open = () => {
    db ??= openDB<OperatorDb>(dbName, 1, {
      upgrade(database) {
        database.createObjectStore(SCAN_STORE_NAME, { keyPath: 'idempotencyKey' });
      },
      // Another tab upgrades the schema: let it proceed, reopen on next use.
      blocking() {
        void db?.then((d) => d.close());
        db = undefined;
      },
    });
    return db;
  };
  return {
    all: async () => (await open()).getAll(SCAN_STORE_NAME),
    get: async (key) => (await open()).get(SCAN_STORE_NAME, key),
    put: async (scan) => void (await (await open()).put(SCAN_STORE_NAME, scan)),
    delete: async (key) => (await open()).delete(SCAN_STORE_NAME, key),
  };
}

/** In-memory store (tests); values are cloned like IndexedDB's structured clone. */
export function createMemoryScanStore(initial: QueuedScan[] = []): ScanStore & { snapshot(): QueuedScan[] } {
  const items = new Map(initial.map((s) => [s.idempotencyKey, structuredClone(s)]));
  return {
    all: async () => [...items.values()].map((s) => structuredClone(s)),
    get: async (key) => {
      const s = items.get(key);
      return s && structuredClone(s);
    },
    put: async (scan) => void items.set(scan.idempotencyKey, structuredClone(scan)),
    delete: async (key) => void items.delete(key),
    snapshot: () => [...items.values()].map((s) => structuredClone(s)),
  };
}
